import { ReactNode, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { usePwlJug } from '@hooks/register/use-pwl-jug';
import { useLocalize } from '@hooks/use-localize';
import { useSession } from '@hooks/security/use-session';
import { FormProvider, useForm } from 'react-hook-form';
import { ILabOrdersFormValues } from '@/types/history/lab-orders';
import { labOrdersSchema } from './lab-orders.schema';
import { zodResolver } from '@hookform/resolvers/zod';
import { asStringBool } from '@/utils/parse';
import type { TAddressBanner } from '@views/register/pwl/pwl-jug-sections/edit-address-modal';
import type { IMailingAddress, IPwlJugModel, TPwlJugMailOption, TPwlJugStatus } from '@/types/history/pwl-jug';
import EditAddressModal from '@views/register/pwl/pwl-jug-sections/edit-address-modal';
import MentorOfferSection from './lab-orders-sections/mentor-offer-section';

const PwlJug = () => {
    const { registrationId, editKey } = useSession();
    const { model, isLoading, error, submit, isSubmitting, submitError, refresh } = usePwlJug(registrationId, editKey);

    const navigate = useNavigate();

    const [addressBanner, setAddressBanner] = useState<TAddressBanner | null>(null);
    const [bannerStatus, setBannerStatus] = useState<TPwlJugStatus>(model.status);

    // The address banners open when the loaded status says so, then the donor drives them.
    if (model.status !== bannerStatus) {
        setBannerStatus(model.status);

        if (model.status === 'addressNotValid') {
            setAddressBanner('notValid');
        } else if (model.status === 'addressNeedsMoreInfo') {
            setAddressBanner('needsMoreInfo');
        }
    }

    useEffect(() => {
        switch (model.status) {
            case 'labsComplete':
                navigate('/register/donate', { replace: true });
                return;
            case 'alreadyShipped':
            case 'labsStarted':
                navigate('/register/lab-orders', { replace: true });
                return;
            // Still a Razor page, so this one needs a full navigation.
            case 'pwnValidationFailed':
                window.location.assign('/register/pwn-retry-form?id=' + model.id + '&key=' + model.editKey);
                return;
        }
    }, [model.status, model.id, model.editKey, navigate]);

    if (isLoading) {
        return (
            <div className="row">
                <div className="col-xs-12">Loading&hellip;</div>
            </div>
        );
    }

    return (
        <>
            {error && (
                <div className="row">
                    <div className="col-xs-12">
                        <div className="alert alert-danger">{error}</div>
                    </div>
                </div>
            )}

            <PwlJugContent
                model={model}
                onSelect={submit}
                isSubmitting={isSubmitting}
                submitError={submitError}
                onEditAddress={() => setAddressBanner('none')}
            />

            {addressBanner !== null && (
                <EditAddressModal
                    registrationId={model.id}
                    editKey={model.editKey}
                    banner={addressBanner}
                    onClose={() => setAddressBanner(null)}
                    onSaved={refresh}
                />
            )}
        </>
    );
};

interface IPwlJugContentProps {
    model: IPwlJugModel;
    onSelect: (mailOption: TPwlJugMailOption) => void;
    isSubmitting: boolean;
    submitError: string | null;
    onEditAddress: () => void;
}

const PwlJugContent = ({
    model,
    onSelect,
    isSubmitting,
    submitError,
    onEditAddress,
}: IPwlJugContentProps) => {
    const t = useLocalize(model.languageData);

    const methods = useForm<ILabOrdersFormValues>({
        resolver: zodResolver(labOrdersSchema),
        defaultValues: {
            labChoice: '',
            connectToDonorIsDesired: asStringBool(model.connectToDonorIsDesired),
        },
    });

    if (model.status === 'waiting') {
        return (
            <div className="row">
                <div className="col-xs-12">
                    <h4>
                        {t(
                            'DashLabel_AlrightYouReAllDoneWeLlCheckWithYouAgainInAboutAMonthToSee',
                            "Alright. You're all done. We'll check with you again in about a month to see if you are ready for the labs",
                        )}
                        .
                    </h4>
                </div>
            </div>
        );
    }

    const sendToAddress = t(
        'DashLabel_YesSendThe24HourUrineJugToThisAddress',
        'Yes, send the 24-hour urine jug to this address',
    );

    return (
        <div className='container'>
            <div className="row">
                <div className='col-xs-7'>
                    <div>
                        <h4>
                            {t(
                                'DashLabel_ThankYouForCompletingTheMedicalHistoryQuestionnaire',
                                'Thank you for completing the Medical History Questionnaire',
                            )}
                            .
                        </h4>

                        <p>
                            {t(
                                'DashLabel_NowThatYourMedicalHistoryHasBeenAcceptedYouCanBeginTheLab',
                                'Now that your medical history has been accepted you can begin the lab testing process. You will not be charged for any of the following tests. You will need to complete a 24-hour urine collection test, a blood test, get your height, weight and blood pressure recorded and give a urine sample. These are all tests that would have to be done were you to be worked up the traditional way at a Transplant Center and by completing them through our system you save time and make it easier to find a match once you are referred to a Transplant Center. The first step in this process is for us to ship you a 24-hour urine collection jug',
                            )}
                            .
                        </p>

                        <p>
                            {t(
                                'DashLabel_DoYouThinkYouWouldBeAbleToGoForTheAboveTestsWithinTheNext',
                                'Do you think you would be able to go for the above tests within the next month',
                            )}
                            ?
                        </p>
                    </div>

                    <div>
                        {model.displayAddressChoice && (
                            <AddressChoice
                                title={t('DashLabel_UspsRecommended', 'USPS Recommended')}
                                address={model.usps}>
                                <button
                                    type="button"
                                    className={'btn btn-primary'}
                                    disabled={isSubmitting}
                                    onClick={() => onSelect('usps')}
                                >
                                    {sendToAddress}
                                </button>
                            </AddressChoice>
                        )}

                        <AddressChoice
                            title={t('DashLabel_AddressEntered', 'Address Entered')}
                            address={model.originalEntry}>

                            <div style={{ marginTop: 12 }}>
                                <p style={{ marginBottom: 0 }}>
                                    {t(
                                        'DashLabel_IfYouEnteredYourAddressIncorrectlyPlease',
                                        'If you entered your address incorrectly please',
                                    )}{' '}
                                    <a
                                        href="#"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            onEditAddress();
                                        }}
                                    >
                                        {t('DashLabel_ClickHereToEdit', 'click here to edit')}
                                    </a>
                                </p>
                            </div>
                        </AddressChoice>
                    </div>

                    {submitError && (
                        <div>
                            <div className="alert alert-danger">{submitError}</div>
                        </div>
                    )}

                    <button
                        type="button"
                        className={model.displayAddressChoice ? 'btn btn-info' : 'btn btn-special-primary'}
                        disabled={isSubmitting}
                        onClick={() => onSelect('input')}
                    >
                        {sendToAddress}
                    </button>

                    <div style={{ marginTop: 10, textAlign: 'left' }}>
                        <div style={{ marginBottom: 10 }}>
                            <button
                                type="button"
                                className="btn btn-warning"
                                disabled={isSubmitting}
                                onClick={() => onSelect('none')}
                            >
                                {t('DashLabel_NoRemindMeIn1Month', 'No, remind me in 1 month')}
                            </button>
                        </div>
                    </div>

                    <div className="journey-opt-out">
                        You may withdraw at any time using this link: <Link to={'/register/opt-out/' + model.id + '/' + model.editKey}>Opt-out of the donation process</Link>
                    </div>
                </div>

                <div className="col-xs-12 col-sm-5">
                    <FormProvider {...methods}>
                        {model.showMentorOffer && <MentorOfferSection t={t} />}
                    </FormProvider>
                </div>
            </div>
        </div>
    );
};

interface IAddressChoiceProps {
    title: string;
    address: IMailingAddress;
    // buttonClassName: string;
    // buttonText: string;
    // disabled: boolean;
    children: ReactNode;
    // onSelect: () => void;
}

const AddressChoice = ({
    title,
    address,
    // buttonClassName,
    // buttonText,
    // disabled,
    children,
    // onSelect,
}: IAddressChoiceProps) => (
    <div>
        <div className="well">
            <h3 style={{ marginTop: 0 }}>{title}</h3>
            <div>{address.streetAddress}</div>
            <div>{address.cityStateZip}</div>
            <div style={{ marginTop: 12 }}>
                {/* <button
                    type="button"
                    className={'btn ' + buttonClassName}
                    disabled={disabled}
                    onClick={onSelect}
                >
                    {buttonText}
                </button> */}
                {children}
            </div>
        </div>
    </div>
);

export default PwlJug;
